import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

const SIZES = ["S", "M", "L", "XL"];

const fmtImg = (b) => (b ? (b.startsWith("data:") ? b : `data:image/png;base64,${b}`) : null);

// Public whole-shop manufacturer spec sheet. One long, print-friendly document
// with every design's factory notes + reference images. Designed to be sent
// to a factory as a single PDF via Ctrl+P.
const ManufacturerSpecAll = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/spec-all")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Spec sheet not available"));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-[10px] tracking-[0.3em] uppercase text-red-600 mb-2">// SPEC SHEET UNAVAILABLE</div>
          <p className="text-sm text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">// LOADING FULL SHOP SPEC…</div>
      </div>
    );
  }

  const generated = new Date(data.generated_at).toISOString().slice(0, 10);

  return (
    <div data-testid="manufacturer-spec-all-page" className="min-h-screen bg-white text-black print:bg-white">
      {/* Cover header */}
      <header className="border-b-4 border-black bg-black text-white px-8 py-8 print:break-after-page">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#d4af37] mb-2">// FULL CATALOGUE · MANUFACTURING SPEC · CONFIDENTIAL</div>
          <div className="font-display text-4xl md:text-5xl text-[#d4af37]">RAW COMPANY CLOTHING</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mt-2">
            Parent: RAW COMPANY TECH LIMITED · Companies House 17349121
          </div>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-[10px] tracking-[0.3em] uppercase text-zinc-400">
            <div>
              <div className="text-zinc-500 mb-1">Generated</div>
              <div className="text-white font-mono">{generated}</div>
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Total Designs</div>
              <div className="text-[#d4af37] font-mono text-lg">{data.total_designs}</div>
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Total Units</div>
              <div className="text-[#d4af37] font-mono text-lg">{data.total_units}</div>
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Site</div>
              <div className="text-white normal-case tracking-normal">rawcompanyclothing.uk</div>
            </div>
          </div>
          <p className="mt-6 text-xs text-zinc-300 leading-relaxed max-w-3xl normal-case tracking-normal">
            This document is the complete factory brief for every design currently in the Raw Company Clothing catalogue.
            Each garment section that follows contains its production quantities, laid-flat dimensions per size, panel
            materials, factory notes and reference images (front / back / side). Branding rules stated on each page are
            mandatory and identical across all garments.
          </p>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-8 py-10 print:py-6">
        {/* Contents list */}
        <section className="mb-10 print:break-after-page">
          <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// CONTENTS</div>
          <ol className="border-2 border-black divide-y divide-zinc-300">
            {data.products.map((p, i) => (
              <li key={p.product_id || i} className="flex items-center justify-between p-3 text-sm">
                <span className="flex items-center gap-3">
                  <span className="font-mono text-[#c99a1a] w-8 text-right">{String(i + 1).padStart(2, "0")}.</span>
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                    Drop {String(p.drop_number || 1).padStart(2, "0")} · {p.clothing_type} · {p.gender}
                  </span>
                </span>
                <span className="font-mono text-xs text-zinc-500">{p.total_units} units</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Per-product sections */}
        {data.products.map((spec, idx) => (
          <SpecBlock key={spec.product_id || idx} spec={spec} index={idx + 1} total={data.products.length} />
        ))}
      </main>

      {/* Footer */}
      <footer className="border-t-4 border-black bg-black text-white px-8 py-6 mt-10">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[10px] tracking-[0.3em] uppercase text-zinc-400">
          <div>
            © {new Date().getFullYear()} <span className="text-white">RAW COMPANY TECH LIMITED</span> ·
            <span className="text-[#d4af37] normal-case tracking-normal ml-1">Companies House 17349121</span>
          </div>
          <a href="https://rawcompanyclothing.uk" className="text-[#d4af37] hover:underline">rawcompanyclothing.uk</a>
        </div>
      </footer>

      {/* Print button + Download button — hidden when printing */}
      <div className="fixed bottom-6 right-6 print:hidden flex flex-col gap-2 items-end">
        <a
          href="/api/spec-all/download"
          download
          data-testid="spec-all-download-btn"
          className="bg-[#d4af37] text-black border-2 border-[#d4af37] px-5 py-3 text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-black hover:text-[#d4af37]"
        >
          ⬇ DOWNLOAD BLUEPRINT (ZIP)
        </a>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="spec-all-print-btn"
          className="bg-black text-[#d4af37] border-2 border-[#d4af37] px-5 py-3 text-[10px] tracking-[0.3em] uppercase font-bold hover:bg-[#d4af37] hover:text-black"
        >
          ⎙ Print full catalogue
        </button>
      </div>
    </div>
  );
};

const SpecBlock = ({ spec, index, total }) => {
  const dims = spec.dimensions || {};
  return (
    <section
      data-testid={`spec-block-${spec.product_id || index}`}
      className="border-t-4 border-black pt-8 pb-12 mt-8 first:mt-0 first:border-t-0 first:pt-0 print:break-before-page"
    >
      <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
        <span>Design {String(index).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
        <span>Spec ID · <span className="font-mono text-[#c99a1a]">{spec.spec_id}</span></span>
      </div>
      <h2 className="font-display text-3xl md:text-4xl mb-2">{spec.name}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
        <SpecKV label="Garment" value={`${spec.clothing_type}${spec.garment_style ? ` · ${spec.garment_style}` : ""}`} />
        <SpecKV label="Gender" value={spec.gender} />
        <SpecKV label="Colour" value={spec.color || "—"} />
        <SpecKV label="Fabric" value={spec.material || "—"} />
      </div>
      {spec.description && (
        <p className="mt-4 text-sm text-zinc-700 leading-relaxed max-w-3xl">{spec.description}</p>
      )}

      {/* Reference images */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 print:break-inside-avoid">
        {[
          { label: "Front", src: fmtImg(spec.image_front) },
          { label: "Back", src: fmtImg(spec.image_back) },
          { label: "Side", src: fmtImg(spec.image_side) },
        ].map((v) => (
          <div key={v.label} className="border-2 border-black">
            <div className="text-[10px] tracking-[0.3em] uppercase bg-black text-white p-2 text-center">{v.label} View</div>
            <div className="aspect-square bg-zinc-100 flex items-center justify-center">
              {v.src ? (
                <img src={v.src} alt={`${v.label} view of ${spec.name}`} className="w-full h-full object-contain" />
              ) : (
                <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">// no image supplied</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Unit split */}
      <div className="mt-6 border-2 border-black p-5 print:break-inside-avoid">
        <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-1">// PRODUCTION ORDER</div>
        <h3 className="font-display text-xl mb-4">
          {spec.units_per_size} × each size · {spec.total_units} units total
        </h3>
        <div className="grid grid-cols-4 gap-3">
          {SIZES.map((s) => {
            const row = (spec.unit_split || []).find((x) => x.size === s);
            return (
              <div key={s} className="border border-black p-3 text-center">
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">Size</div>
                <div className="font-display text-2xl mb-1">{s}</div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">Quantity</div>
                <div className="font-mono text-xl text-[#c99a1a]">× {row?.quantity ?? spec.units_per_size}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dimensions table */}
      {(dims.S || dims.M || dims.L || dims.XL) && (
        <div className="mt-6 print:break-inside-avoid">
          <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// DIMENSIONS (CM)</div>
          <div className="border-2 border-black overflow-x-auto">
            <table className="w-full font-mono text-sm">
              <thead>
                <tr className="border-b-2 border-black bg-black text-white">
                  <th className="text-left p-3 text-[10px] tracking-[0.3em] uppercase">Measurement</th>
                  {SIZES.map((s) => <th key={s} className="text-center p-3 text-[10px] tracking-[0.3em] uppercase">{s}</th>)}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const rows = new Set();
                  SIZES.forEach((s) => Object.keys(dims[s] || {}).forEach((k) => rows.add(k)));
                  const sortedRows = Array.from(rows);
                  if (sortedRows.length === 0) {
                    return (
                      <tr>
                        <td colSpan={5} className="p-4 text-center text-zinc-500 text-sm">
                          No dimensions recorded for this design yet.
                        </td>
                      </tr>
                    );
                  }
                  return sortedRows.map((k) => (
                    <tr key={k} className="border-b border-zinc-300 last:border-b-0">
                      <td className="p-3 text-zinc-800">{k}</td>
                      {SIZES.map((s) => (
                        <td key={s} className="p-3 text-center">
                          {dims[s]?.[k] ? <span className="text-[#c99a1a]">{dims[s][k]}</span> : <span className="text-zinc-400">—</span>}
                        </td>
                      ))}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Panel materials */}
      {Array.isArray(spec.materials) && spec.materials.length > 0 && (
        <div className="mt-6 print:break-inside-avoid">
          <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// PANEL MATERIALS</div>
          <div className="border-2 border-black">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-black bg-black text-white">
                  <th className="text-left p-3 text-[10px] tracking-[0.3em] uppercase">Panel</th>
                  <th className="text-left p-3 text-[10px] tracking-[0.3em] uppercase">Material</th>
                </tr>
              </thead>
              <tbody>
                {spec.materials.map((m, i) => (
                  <tr key={i} className="border-b border-zinc-300 last:border-b-0">
                    <td className="p-3 text-zinc-800">{m.panel || "—"}</td>
                    <td className="p-3 text-zinc-800">{m.material || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Factory notes */}
      {spec.factory_notes && (
        <div className="mt-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-black mb-3">// FACTORY NOTES</div>
          <pre className="border-2 border-black p-5 bg-zinc-50 text-xs text-zinc-800 whitespace-pre-wrap font-mono leading-relaxed">
            {spec.factory_notes}
          </pre>
        </div>
      )}
    </section>
  );
};

const SpecKV = ({ label, value }) => (
  <div>
    <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">{label}</div>
    <div className="text-zinc-900 mt-0.5">{value}</div>
  </div>
);

export default ManufacturerSpecAll;
